import React from "react";
import { Card, Elevation, H3, Text, InputGroup, Button, Spinner, NonIdealState, Tag, Icon } from "@blueprintjs/core";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { apiCallGet, apiCallPost } from "../../../../micro/fns/util";
import cogoToast from "cogo-toast";
import moment from "moment";
import { DateInput } from "@blueprintjs/datetime";

class AuditTab extends React.Component {
  state = {
    isLoading: false,
    logs: [],
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false,
    quickFilterText: "",
    gridApi: null,
    dateRange: null,
    appliedFilters: {},
    // Date filters (default to today)
    startDate: moment().startOf("day").toDate(),
    endDate: moment().endOf("day").toDate(),
    surgeryScheduledDate: null,
    surgeryScheduledTime: null,
  };

  // Method to refresh audit data - called by parent component
  refreshAuditData = () => {
    console.log("AuditTab refreshAuditData called");
    this.loadAuditLogs(1);
  };

  componentDidMount() {
    console.log("AuditTab componentDidMount");
    console.log("forwardedRef:", this.props.forwardedRef);
    // Initialize start date from surgery createdAt if available
    this.updateStartDateFromSurgery().then((updated) => {
      if (!updated) {
        this.loadAuditLogs(1);
      }
    });

    // Set up the ref for parent component access
    if (this.props.forwardedRef) {
      console.log("Setting up forwardedRef.current");
      this.props.forwardedRef.current = this;
      console.log("forwardedRef.current set to:", this.props.forwardedRef.current);
    } else {
      console.log("No forwardedRef provided");
    }
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.match?.params?.id;
    const currId = this.props.match?.params?.id;
    if (prevId !== currId && currId) {
      // When surgery changes, re-initialize start date from its createdAt
      this.updateStartDateFromSurgery();
    }

    // React to refreshTick changes from parent
    if (this.props.refreshTick !== prevProps.refreshTick) {
      this.loadAuditLogs(1);
    }

    // If parent ever passes surgery prop and its createdAt changes, update start date
    const prevCreatedAt = prevProps.surgery?.createdAt;
    const currCreatedAt = this.props.surgery?.createdAt;
    if (currCreatedAt && prevCreatedAt !== currCreatedAt) {
      this.updateStartDateFromSurgery();
    }
  }

  componentWillUnmount() {
    // Clean up the ref
    if (this.props.forwardedRef) {
      this.props.forwardedRef.current = null;
    }
  }

  getSurgeryId = () => {
    return this.props.match?.params?.id;
  };

  // Date formatting helpers for DateInput
  formatDate = (date) => {
    if (!date) return "";
    return moment(date).format("DD/MM/YYYY");
  };

  parseDate = (dateString) => {
    if (!dateString) return null;
    const parsed = moment(dateString, "DD/MM/YYYY");
    return parsed.isValid() ? parsed.toDate() : null;
  };

  // Initialize/refresh startDate from surgery.createdAt (via prop or API), then load logs
  updateStartDateFromSurgery = async () => {
    try {
      const surgeryId = this.getSurgeryId();
      if (!surgeryId) return false;

      // Prefer createdAt from props if parent provided surgery
      const createdAtFromProps = this.props.surgery?.createdAt;
      const scheduledDateFromProps =
        this.props.surgery?.scheduledDate || this.props.surgery?.date || null;
      const scheduledTimeFromProps =
        this.props.surgery?.scheduledTime || this.props.surgery?.time || null;
      if (createdAtFromProps) {
        const start = moment(createdAtFromProps).startOf("day").toDate();
        this.setState(
          {
            startDate: start,
            page: 1,
            surgeryScheduledDate: scheduledDateFromProps,
            surgeryScheduledTime: scheduledTimeFromProps,
          },
          () => this.loadAuditLogs(1)
        );
        return true;
      }

      // Fallback: fetch booking to get createdAt
      const url = `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getById/${surgeryId}`;
      const response = await apiCallGet(url);
      if (response?.success && response.data?.createdAt) {
        const start = moment(response.data.createdAt).startOf("day").toDate();
        const scheduledDateFromApi = response.data.scheduledDate || response.data.date || null;
        const scheduledTimeFromApi = response.data.scheduledTime || response.data.time || null;
        this.setState(
          {
            startDate: start,
            page: 1,
            surgeryScheduledDate: scheduledDateFromApi,
            surgeryScheduledTime: scheduledTimeFromApi,
          },
          () => this.loadAuditLogs(1)
        );
        return true;
      }
    } catch (err) {
      console.error("Failed to set start date from surgery createdAt:", err);
      // Silent fallback handled by caller or future refreshes
    }
    return false;
  };

  loadAuditLogs = async (page = this.state.page) => {
    const surgeryId = this.getSurgeryId();
    if (!surgeryId) return;

    try {
      this.setState({ isLoading: true });
      const url = `/v3/${this.props.authStore.prefix}/neoSxAuditLog/search/${surgeryId}`;
      const response = await apiCallPost(url, {
        page,
        limit: this.state.limit,
        startDate: moment(this.state.startDate).toISOString(),
        endDate: moment(this.state.endDate).toISOString(),
      });

      if (response?.success) {
        const data = response.data || {};
        this.setState({
          logs: Array.isArray(data.logs) ? data.logs : [],
          page: data.pagination?.page || page,
          limit: data.pagination?.limit || this.state.limit,
          total: data.pagination?.total || 0,
          pages: data.pagination?.pages || 0,
          hasNext: !!data.pagination?.hasNext,
          hasPrev: !!data.pagination?.hasPrev,
          dateRange: data.dateRange || null,
          appliedFilters: data.appliedFilters || {},
          isLoading: false,
        });
      } else {
        throw new Error(response?.message || "Failed to load audit logs");
      }
    } catch (err) {
      console.error("Error loading audit logs:", err);
      cogoToast.error(err.message || "Failed to load audit logs");
      this.setState({
        logs: [],
        total: 0,
        pages: 0,
        isLoading: false,
      });
    }
  };

  columnDefs = [
    {
      headerName: "Date & Time",
      field: "createdAt",
      width: 180,
      pinned: "left",
      cellRenderer: (params) => {
        if (!params.value) return "";
        return moment(params.value).format("DD/MM/YYYY HH:mm:ss");
      },
    },
    {
      headerName: "Action",
      field: "actionType",
      width: 120,
      cellRenderer: (params) => {
        const getActionColor = (action) => {
          switch (action) {
            case "CREATE":
              return "#0F9960";
            case "UPDATE":
              return "#137CBD";
            case "DELETE":
              return "#DB3737";
            case "UPDATE_PRICE":
              return "#D9822B";
            default:
              return "#5C7080";
          }
        };
        return `<span style="color: ${getActionColor(params.value)}; font-weight: 600;">${params.value || ""}</span>`;
      },
    },
    {
      headerName: "Description",
      field: "actionSlug",
      width: 320,
      wrapText: true,
      autoHeight: true,
      cellRenderer: (params) => {
        let desc = params.value || "";
        const meta = params.data?.actionMetadata;
        if (
          desc.startsWith("Created surgery booking") &&
          Array.isArray(meta?.selectedSurgeries) &&
          meta.selectedSurgeries.length > 0
        ) {
          desc += `<br/><span style="color: var(--text-secondary); font-size: 11px;">Surgeries: ${meta.selectedSurgeries.join(", ")}</span>`;
        }
        const isPostponedStatus =
          meta?.fieldName === "status" &&
          String(meta?.newValue || "").toLowerCase() === "postponed";
        if (isPostponedStatus && meta?.postponementNotes) {
          const safeNotes = String(meta.postponementNotes)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
          const formattedNotes = safeNotes.replace(/\n/g, "<br/>");
          desc += `<br/><span style="color: var(--text-secondary); font-size: 11px;">Postponement notes: ${formattedNotes}</span>`;
        }
        const isCancellation =
          String(desc).toLowerCase().includes("cancelled surgery booking");
        const cancellationDate =
          meta?.scheduledDate ||
          meta?.date ||
          this.state.surgeryScheduledDate ||
          meta?.originalScheduledDate ||
          null;
        if (isCancellation && cancellationDate) {
          const formattedCancelDate = moment(cancellationDate).isValid()
            ? moment(cancellationDate).format("DD/MM/YYYY")
            : "";
          if (formattedCancelDate) {
            if (/scheduled on/i.test(desc)) {
              desc = desc.replace(
                /scheduled on[^<]*/i,
                `scheduled on ${formattedCancelDate}`
              );
            } else {
              desc += `<br/><span style="color: var(--text-secondary); font-size: 11px;">Scheduled date: ${formattedCancelDate}</span>`;
            }
          }
        }
        if (isCancellation && meta?.cancellationReason) {
          const safeReason = String(meta.cancellationReason)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
          const formattedReason = safeReason.replace(/\n/g, "<br/>");
          desc += `<br/><span style="color: var(--text-secondary); font-size: 11px;">Cancellation reason: ${formattedReason}</span>`;
        }
        const rawBillingMeta = meta?.newValue || meta?.previousValue || meta || {};
        let billingMeta = rawBillingMeta;
        if (typeof rawBillingMeta === "string") {
          try {
            billingMeta = JSON.parse(rawBillingMeta);
          } catch (e) {
            billingMeta = {};
          }
        }
        const isBillingLog =
          params.data?.resourceType === "SURGERY_BILLING" ||
          billingMeta?.panel === "BILLING" ||
          String(meta?.fieldName || "").toLowerCase().includes("payments");
        if (isBillingLog) {
          const amountPaise = Number(billingMeta?.amountPaise);
          const amountRupees = Number.isFinite(amountPaise)
            ? amountPaise / 100
            : Number(billingMeta?.amount);
          if (Number.isFinite(amountRupees)) {
            const amountText = amountRupees.toFixed(2);
            desc += `<br/><span style="color: var(--text-secondary); font-size: 11px;">Amount: INR ${amountText}</span>`;
          }
          const remarks = billingMeta?.paymentRemarks || "";
          if (remarks) {
            const safeRemarks = String(remarks)
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")
              .replace(/"/g, "&quot;");
            const formattedRemarks = safeRemarks.replace(/\n/g, "<br/>");
            desc += `<br/><span style="color: var(--text-secondary); font-size: 11px;">Remarks: ${formattedRemarks}</span>`;
          }
        }
        return `<div style="white-space: normal; line-height: 1.3; padding: 4px 0;">${desc}</div>`;
      },
    },
    { headerName: "Resource", field: "resourceType", width: 140 },
    { headerName: "Resource Name", field: "resourceName", width: 200 },
    { headerName: "Actor", field: "actorName", width: 160 },
    { headerName: "Email", field: "actorEmail", width: 220 },
  ];

  renderHeaderInfo = () => {
    const { dateRange, appliedFilters, total } = this.state;
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Tag minimal intent="primary">
          Total: {total}
        </Tag>
        {appliedFilters?.actionType && <Tag minimal>Action: {appliedFilters.actionType}</Tag>}
        {appliedFilters?.resourceType && <Tag minimal>Resource: {appliedFilters.resourceType}</Tag>}
        {dateRange && (
          <Tag minimal intent="warning">
            {moment(dateRange.startDate).format("DD/MM/YYYY")} - {moment(dateRange.endDate).format("DD/MM/YYYY")} (
            {dateRange.daysSpanned} days)
          </Tag>
        )}
      </div>
    );
  };

  renderPagination = () => {
    if (this.state.pages <= 1) return null;

    const { page, pages, total } = this.state;
    const start = (page - 1) * this.state.limit + 1;
    const end = Math.min(page * this.state.limit, total);

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 0",
          borderTop: "1px solid var(--border-color)",
          marginTop: 12,
        }}
      >
        <div style={{ color: "var(--text-secondary)" }}>
          Showing {start}-{end} of {total}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Button minimal icon="chevron-left" disabled={page === 1} onClick={() => this.loadAuditLogs(page - 1)} />
          {Array.from({ length: Math.min(5, pages) }, (_, i) => {
            let pageNum;
            if (pages <= 5) pageNum = i + 1;
            else if (page <= 3) pageNum = i + 1;
            else if (page >= pages - 2) pageNum = pages - 4 + i;
            else pageNum = page - 2 + i;
            return (
              <Button
                key={pageNum}
                minimal
                intent={page === pageNum ? "primary" : "none"}
                onClick={() => this.loadAuditLogs(pageNum)}
              >
                {pageNum}
              </Button>
            );
          })}
          <Button minimal icon="chevron-right" disabled={page === pages} onClick={() => this.loadAuditLogs(page + 1)} />
        </div>
      </div>
    );
  };

  render() {
    return (
      <div style={{ padding: "20px", paddingTop: "0px" }}>
        {/* <H3 style={{ marginBottom: 12, color: "var(--text-primary)" }}>
          <Icon icon="history" style={{ marginRight: 8 }} /> Audit Trail
        </H3> */}

        <Card elevation={Elevation.ONE} style={{ padding: 12, marginBottom: 12 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto auto",
              gap: 12,
              alignItems: "center",
            }}
          >
            {/* Left: summary tags */}
            <div>{this.renderHeaderInfo()}</div>

            {/* Middle: date filters */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Tag minimal>Start</Tag>
                <DateInput
                  value={this.state.startDate}
                  onChange={(d) => this.setState({ startDate: d, page: 1 }, () => this.loadAuditLogs(1))}
                  format="DD/MM/YYYY"
                  formatDate={this.formatDate}
                  parseDate={this.parseDate}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Tag minimal>End</Tag>
                <DateInput
                  value={this.state.endDate}
                  onChange={(d) => this.setState({ endDate: d, page: 1 }, () => this.loadAuditLogs(1))}
                  format="DD/MM/YYYY"
                  formatDate={this.formatDate}
                  parseDate={this.parseDate}
                />
              </div>
              <Button
                minimal
                onClick={() =>
                  this.setState(
                    { startDate: moment().startOf("day").toDate(), endDate: moment().endOf("day").toDate(), page: 1 },
                    () => this.loadAuditLogs(1)
                  )
                }
              >
                Clear
              </Button>
            </div>

            {/* Right: quick filter + refresh */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
              <InputGroup
                leftIcon="search"
                placeholder="Quick filter..."
                value={this.state.quickFilterText}
                onChange={(e) => {
                  const val = e.target.value;
                  this.setState({ quickFilterText: val });
                  if (this.state.gridApi) this.state.gridApi.setQuickFilter(val);
                }}
                style={{ width: 220 }}
              />
              <Button
                icon="refresh"
                minimal
                onClick={() => this.loadAuditLogs(this.state.page)}
                loading={this.state.isLoading}
              >
                Refresh
              </Button>
            </div>
          </div>
        </Card>

        <Card elevation={Elevation.ONE} style={{ padding: 12 }}>
          {this.state.isLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spinner size={40} />
              <div style={{ marginTop: 10, color: "var(--text-secondary)" }}>Loading audit logs...</div>
            </div>
          ) : this.state.logs.length === 0 ? (
            <NonIdealState
              icon="search"
              title="No Audit Logs Found"
              description="No audit logs available for this surgery."
            />
          ) : (
            <>
              <div className="ag-theme-alpine-dark" style={{ height: 450, width: "100%" }}>
                <AgGridReact
                  columnDefs={this.columnDefs}
                  rowData={this.state.logs}
                  defaultColDef={{ sortable: true, filter: true, resizable: true }}
                  onGridReady={(params) => {
                    this.setState({ gridApi: params.api });
                    params.api.sizeColumnsToFit();
                  }}
                  suppressRowClickSelection={true}
                  headerHeight={40}
                />
              </div>
              {this.renderPagination()}
            </>
          )}
        </Card>
      </div>
    );
  }
}

// Create a forwarded ref version of the AuditTab component
const AuditTabWithRef = React.forwardRef((props, ref) => {
  return <AuditTab {...props} forwardedRef={ref} />;
});

export default inject("authStore")(observer(withRouter(AuditTabWithRef)));
